/**
 * Content Security Policy (CSP) meta tag injection.
 *
 * Adds a `<meta http-equiv="Content-Security-Policy">` tag at app startup.
 * This is a defense-in-depth measure; production should also set the header
 * via reverse proxy / CDN.
 */

export interface CspOptions {
  /** Supabase project URL (for connect-src). */
  supabaseUrl?: string;
  /** Additional connect-src origins (e.g. analytics). */
  extraConnectSrc?: string[];
  /** Additional script-src origins. */
  extraScriptSrc?: string[];
  /** Additional style-src origins. */
  extraStyleSrc?: string[];
}

/**
 * Build a CSP directive string.
 */
export function buildCsp(options: CspOptions = {}): string {
  const connectSrc = [
    "'self'",
    options.supabaseUrl ?? "",
    "https://*.supabase.co",
    "wss://*.supabase.co",
    "https://api.ipify.org", // for IP hashing in audit
    "https://*.ingest.us.sentry.io", // Sentry error reporting
    ...(options.extraConnectSrc ?? []),
  ].filter(Boolean);

  const scriptSrc = [
    "'self'",
    ...(options.extraScriptSrc ?? []),
  ];

  const styleSrc = [
    "'self'",
    "'unsafe-inline'", // needed for Tailwind
    ...(options.extraStyleSrc ?? []),
  ];

  const directives = [
    `default-src 'self'`,
    `script-src ${scriptSrc.join(" ")}`,
    `style-src ${styleSrc.join(" ")}`,
    `connect-src ${connectSrc.join(" ")}`,
    `img-src 'self' data: blob: https:`,
    `font-src 'self' data:`,
    `object-src 'none'`,
    `base-uri 'self'`,
    `form-action 'self'`,
    // frame-ancestors is NOT included here because browsers ignore it
    // in <meta> tags. It must be set via HTTP response header instead.
    // See: https://w3c.github.io/webappsec-csp/#meta-element
  ];

  return directives.join("; ");
}

/**
 * Inject CSP meta tag into `<head>`.
 * Safe to call multiple times — replaces existing tag if present.
 */
export function injectCspMeta(options: CspOptions = {}): void {
  if (typeof document === "undefined") return;

  const csp = buildCsp(options);

  // Remove existing CSP meta if present
  const existing = document.querySelector(
    'meta[http-equiv="Content-Security-Policy"]',
  );
  if (existing) existing.remove();

  const meta = document.createElement("meta");
  meta.httpEquiv = "Content-Security-Policy";
  meta.content = csp;
  document.head.appendChild(meta);
}
